<template>
  <article
    class="topic-card"
    :class="{ 'topic-card--open': isOpen }"
    :data-state="isOpen ? 'open' : 'closed'"
  >
    <button
      type="button"
      class="topic-card__header"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="$emit('toggle-card')"
    >
      <span class="topic-card__icon-wrap" aria-hidden="true">
        <img :src="icon" :alt="title" class="topic-card__icon">
      </span>

      <span class="topic-card__content">
        <DsText as="subtitle7" class="topic-card__title">{{ title }}</DsText>
        <DsText
          as="body4"
          class="topic-card__description"
          :class="{
            'topic-card__description--has-alternate': mobileDescription,
            'topic-card__description--hide-through-tablet': mobileDescriptionBreakpoint === 'tablet'
          }"
        >
          {{ description }}
        </DsText>
        <DsText
          v-if="mobileDescription"
          as="body4"
          class="topic-card__description topic-card__description--alternate"
          :class="{ 'topic-card__description--show-through-tablet': mobileDescriptionBreakpoint === 'tablet' }"
        >
          {{ mobileDescription }}
        </DsText>
      </span>

      <span class="topic-card__arrow" :class="{ 'topic-card__arrow--open': isOpen }" aria-hidden="true">
        <i v-show="isOpen" class="fa-solid fa-chevron-down" />
        <i v-show="!isOpen" class="fa-solid fa-chevron-right" />
      </span>
    </button>

    <div v-show="isOpen" class="topic-card__items">
      <button
        v-for="item in items"
        :key="item.title"
        type="button"
        class="topic-card__item"
        @click="goToRoute(item.routeName)"
      >
        <DsText as="subtitle8" class="topic-card__item-title">{{ item.title }}</DsText>
        <DsText as="body4" class="topic-card__item-description">{{ item.description }}</DsText>
      </button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import DsText from '../atoms/DsText.vue'

interface TopicItem {
  title: string
  description: string
  routeName: string
}

const props = defineProps<{
  icon: string
  title: string
  description: string
  mobileDescription?: string
  mobileDescriptionBreakpoint?: 'mobile' | 'tablet'
  items: TopicItem[]
  isOpen: boolean
}>()

defineEmits<{
  (event: 'toggle-card'): void
}>()

const router = useRouter()

function goToRoute(routeName: string) {
  router.push({ name: routeName })
}
</script>

<style scoped>
.topic-card {
  position: relative;
  border: 1px solid #d9dce3;
  border-radius: 6px;
  background: #fbfbfb;
  overflow: visible;
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}

.topic-card--open {
  z-index: 3;
  box-shadow:
    0 4px 6px -4px rgba(0, 0, 0, 0.1),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.topic-card__header {
  width: 100%;
  min-height: 136px;
  height: 136px;
  padding: 18px 16px;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  border: 0;
  background: transparent;
  box-sizing: border-box;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

.topic-card--open .topic-card__header {
  border-bottom: 1px solid #ececf4;
}

.topic-card__header:hover {
  border-color: rgba(73, 47, 229, 0.35);
  box-shadow: 0 12px 28px rgba(31, 20, 96, 0.08);
  transform: translateY(-1px);
}

.topic-card__icon-wrap {
  width: 30px;
  height: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.topic-card__icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.topic-card__content {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.topic-card__title {
  color: var(--ds-purple-900);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
}

.topic-card__description {
  color: #1f1f1f;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.15;
  max-width: 21ch;
}

.topic-card__description--alternate {
  display: none;
}

.topic-card__arrow {
  color: #111111;
  font-size: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.topic-card__items {
  background: #fbfbfb;
  border-top: 1px solid #ececf4;
}

.topic-card__item {
  width: 100%;
  padding: 14px 20px;
  border: 0;
  border-top: 1px solid #ececf4;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.25s ease;
}

.topic-card__item:first-child {
  border-top: 0;
}

.topic-card__item:hover {
  background: #fafaff;
}

.topic-card__item-title {
  color: var(--ds-purple-900);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
}

.topic-card__item-description {
  color: #1f1f1f;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
}

@media (min-width: 768px) {
  .topic-card__header {
    min-height: 136px;
    height: 136px;
    padding: 24px 28px 24px 20px;
    grid-template-columns: 40px minmax(0, 1fr) 18px;
    gap: 16px;
  }

  .topic-card__content {
    gap: 16px;
  }

  .topic-card__description {
    max-width: 22ch;
  }

  .topic-card__items {
    position: absolute;
    top: calc(100% - 1px);
    left: -1px;
    right: -1px;
    z-index: 2;
    border: 1px solid #d9dce3;
    border-top: 0;
    border-radius: 0 0 12px 12px;
    overflow: hidden;
    box-shadow:
      0 4px 6px -4px rgba(0, 0, 0, 0.1),
      0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
}

@media (max-width: 767px) {
  .topic-card__header {
    min-height: 136px;
    height: 136px;
    padding: 20px 20px 20px 16px;
    gap: 16px;
  }

  .topic-card__description--has-alternate {
    display: none;
  }

  .topic-card__description--alternate {
    display: block;
  }

  .topic-card__description {
    max-width: 22ch;
  }

  .topic-card__items {
    position: static;
    border-radius: 0 0 12px 12px;
    overflow: hidden;
  }

  .topic-card__item {
    padding: 12px 16px;
  }
}

@media (min-width: 768px) and (max-width: 1207px) {
  .topic-card__description--hide-through-tablet {
    display: none;
  }

  .topic-card__description--show-through-tablet {
    display: block;
  }
}
</style>
